import type { QueryClient } from "@tanstack/react-query";
import { isClarityOneMeQuery } from "@/lib/clarityOneMeQuery";

const CUSTOMER_DATA_PREFIXES = ["clarityOneRecords", "kaleidescapeWallet"] as const;

function queryHeadMatchesPrefix(queryKey: unknown, prefix: string): boolean {
  if (!Array.isArray(queryKey) || queryKey.length === 0) return false;
  const head = queryKey[0];
  if (Array.isArray(head)) {
    return head[0] === prefix;
  }
  return head === prefix;
}

function removeQueriesByPrefix(queryClient: QueryClient, prefix: string): void {
  queryClient.removeQueries({
    predicate: (query) => queryHeadMatchesPrefix(query.queryKey, prefix),
  });
}

/**
 * Full Clarity One cache clear, including the active identity query.
 * Use on logout and impersonation handoff before a full-document navigation.
 */
export function clearClarityOneCustomerQueryCache(queryClient: QueryClient): void {
  removeQueriesByPrefix(queryClient, "clarityOneAuth");
  for (const prefix of CUSTOMER_DATA_PREFIXES) {
    removeQueriesByPrefix(queryClient, prefix);
  }
}

/**
 * Customer-scoped data clear that never removes `clarityOneAuth.me`.
 * Use only for a confirmed resolved customer A → different resolved customer B.
 */
export function clearClarityOneCustomerDataCache(queryClient: QueryClient): void {
  queryClient.removeQueries({
    predicate: (query) =>
      queryHeadMatchesPrefix(query.queryKey, "clarityOneAuth") && !isClarityOneMeQuery(query),
  });
  for (const prefix of CUSTOMER_DATA_PREFIXES) {
    removeQueriesByPrefix(queryClient, prefix);
  }
}

function isResolvedCustomerId(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

/**
 * True only for a confirmed switch between two successfully resolved non-null customer IDs.
 * Loading, unresolved, cancellation, and error (null/undefined) are not identity changes.
 */
export function didClarityOneResolvedCustomerSwitch(
  previousCustomerId: number | null | undefined,
  nextCustomerId: number | null | undefined,
): boolean {
  return (
    isResolvedCustomerId(previousCustomerId) &&
    isResolvedCustomerId(nextCustomerId) &&
    previousCustomerId !== nextCustomerId
  );
}

/** @deprecated Use didClarityOneResolvedCustomerSwitch. Same semantics: null/loading is not a switch. */
export function didClarityOneIdentityChange(
  previousCustomerId: number | null | undefined,
  nextCustomerId: number | null | undefined,
): boolean {
  return didClarityOneResolvedCustomerSwitch(previousCustomerId, nextCustomerId);
}

export function applyClarityOneResolvedCustomerSwitch(
  queryClient: QueryClient,
  previousCustomerId: number | null | undefined,
  nextCustomerId: number | null | undefined,
): boolean {
  if (!didClarityOneResolvedCustomerSwitch(previousCustomerId, nextCustomerId)) {
    return false;
  }
  clearClarityOneCustomerDataCache(queryClient);
  return true;
}
